import React from 'react';
import PropTypes from 'prop-types';
import { AppContext } from '@prisjakt/ui-app-context';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';
import queryString from 'querystring';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { createExperimentHook } from '@prisjakt/ui-experiment';

import actions from '../../actions/search';

import Facet from './facet';

const useCategoryLinkExperiment = createExperimentHook(
  'search-category-link',
  {},
  {
    linkToCategory: {
      category_id: {
        link: 'category',
      },
      brand_id: {
        hide: true,
      },
    },
  }
);

const CategoryExperimentWrapper = ({ children }) => {
  const linkToCategoryExperiment = useCategoryLinkExperiment();

  return children(linkToCategoryExperiment);
};

const DELIMITER = ',';
const adsViewUpdate = () => ({
  type: 'ADS_VIEW_UPDATE',
  view: 'search_results_recieved',
});

class Facets extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      limits: {},
      currentSelection: this.getProductFacets(this.props.location),
    };
  }

  componentDidMount() {
    this.unlisten = this.props.history.listen(location => {
      this.setState({ currentSelection: this.getProductFacets(location) });
    });
  }

  componentWillUnmount() {
    this.unlisten();
  }

  static getSelectedFromUrl(facet, qs) {
    const facetFromUrl = qs[facet.id] || '';

    switch (facet.type) {
      case 'SearchFacetList': {
        return {
          selected: facetFromUrl.split(',').filter(x => x),
        };
      }
      case 'SearchFacetInterval': {
        const [from, to] = facetFromUrl.split(',');
        return {
          from: parseInt(from, 10) || undefined,
          to: parseInt(to, 10) || undefined,
        };
      }
      default: {
        return {};
      }
    }
  }

  getProductFacets(location) {
    const qs = queryString.decode(location.search.substr(1));

    return this.props.facets.reduce(
      (all, facet) => ({
        ...all,
        [facet.id]: {
          id: facet.id,
          type: facet.type,
          ...Facets.getSelectedFromUrl(facet, qs),
        },
      }),
      {}
    );
  }

  updateUrl(newFacets, location) {
    const qs = queryString.decode(location.search.substr(1));
    const { history } = this.props;

    Object.keys(newFacets).forEach(id => {
      const facet = newFacets[id];
      switch (facet.type) {
        case 'SearchFacetList': {
          if (facet.selected.length === 0) {
            delete qs[id];
          } else {
            qs[id] = facet.selected.join(DELIMITER);
          }
          break;
        }
        case 'SearchFacetInterval': {
          if (!facet.from && !facet.to) {
            delete qs[id];
          } else {
            qs[id] = [facet.from, facet.to].join(DELIMITER);
          }
          break;
        }
        default:
      }
    });

    delete qs.offset;

    history.replace(`${location.pathname}?${queryString.encode(qs)}`);
  }

  toggleLimit = id => {
    this.setState(prevState => ({
      limits: {
        ...prevState.limits,
        [id]: Infinity,
      },
    }));
  };

  toggleFacet = (id, type, value) => {
    const newFacets = this.state.currentSelection;

    switch (type) {
      // Handle list facets
      case 'SearchFacetList': {
        const parsedId = String(value);
        const { selected } = newFacets[id];
        if (selected.includes(parsedId)) {
          newFacets[id].selected = selected.filter(x => x !== parsedId);
        } else {
          newFacets[id].selected = selected.concat(parsedId);
        }
        break;
      }

      case 'SearchFacetInterval': {
        newFacets[id].from = value.from;
        newFacets[id].to = value.to;
        break;
      }
      default: {
        break;
      }
    }

    this.updateUrl(newFacets, this.props.location);
    this.props.searchFilter(id);
    this.props.adsViewUpdate();
  };

  render() {
    const { facets, inactiveTypes, showSubtitle, maxListItems } = this.props;
    const { t } = this.context;
    const { currentSelection } = this.state;

    if (!facets) {
      return null;
    }

    return (
      <CategoryExperimentWrapper>
        {linkToCategoryExperiment => {
          return facets
            .filter(facet => {
              if (inactiveTypes.includes(facet.type)) {
                return false;
              }

              if (facet.type === 'SearchFacetList') {
                return facet.buckets.length !== 0;
              }

              if (facet.type === 'SearchFacetInterval') {
                return facet.maxCount > 0;
              }

              return true;
            })
            .map(facet => {
              const { link, hide } = linkToCategoryExperiment[facet.id] || {
                link: false,
                hide: false,
              };

              if (hide) {
                return null;
              }

              return (
                <Panel
                  key={facet.id}
                  header={
                    <PanelHeader
                      title={t('search', `facets.title-${facet.id}`)}
                      subtitle={
                        showSubtitle &&
                        facet.list &&
                        t('search', 'results.n-results', {
                          n: facet.list.length,
                        })
                      }
                    />
                  }
                >
                  <Facet
                    currentSelection={currentSelection[facet.id]}
                    onToggleFacet={this.toggleFacet}
                    onToggleLimit={this.toggleLimit}
                    facet={facet}
                    link={link}
                    showMoreButton={maxListItems}
                    maxListItems={this.state.limits[facet.id] || maxListItems}
                  />
                </Panel>
              );
            });
        }}
      </CategoryExperimentWrapper>
    );
  }
}

const { func, bool, arrayOf, string, number, shape, object } = PropTypes;

Facets.propTypes = {
  facets: arrayOf(
    shape({
      type: string,
      from: number,
      to: number,
      list: arrayOf(string),
    })
  ).isRequired,
  onToggleFacet: func.isRequired,
  showSubtitle: bool.isRequired,
  inactiveTypes: arrayOf(string),
  location: object.isRequired,
  history: object.isRequired,
  maxListItems: number.isRequired,
  searchFilter: func.isRequired,
  adsViewUpdate: func.isRequired,
};

Facets.defaultProps = {
  inactiveTypes: [],
};

Facets.contextType = AppContext;

export default withRouter(
  connect(undefined, { ...actions, adsViewUpdate })(Facets)
);
